import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Copy, Download, Expand, FileText,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { reportsApi } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { sanitizeEmailHtml } from "@/components/inbox/MessageContent";
import { stripHtml } from "@/lib/emailComposer";
import { cn } from "@/lib/utils";

function DetailSection({ title, children, actions }) {
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[12px] font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
        {actions}
      </div>
      <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 text-[13px] text-slate-700 space-y-1.5">
        {children}
      </div>
    </section>
  );
}

DetailSection.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  actions: PropTypes.node,
};

function Row({ label, value }) {
  return (
    <div className="flex gap-3 justify-between">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="text-right break-all">{value || "—"}</span>
    </div>
  );
}

Row.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

function deliveryBadgeClass(status) {
  if (status === "sent") return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  if (status === "failed") return "bg-red-50 text-red-700 ring-red-200";
  if (status === "queued") return "bg-sky-50 text-sky-700 ring-sky-200";
  return "bg-amber-50 text-amber-800 ring-amber-200";
}

function EmailPreviewFrame({ html, text, className }) {
  const safeHtml = useMemo(() => {
    if (html && String(html).trim()) return sanitizeEmailHtml(html);
    if (text && String(text).trim()) {
      const escaped = String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      return `<pre style="white-space:pre-wrap;font-family:inherit;margin:0">${escaped}</pre>`;
    }
    return "";
  }, [html, text]);

  if (!safeHtml) {
    return <p className="m-0 text-slate-500 text-[13px]">No stored email preview is available for this lead.</p>;
  }

  return (
    <div
      className={cn(
        "rounded-md border border-slate-200 bg-white p-3 overflow-auto prose prose-sm max-w-none text-slate-800",
        className,
      )}
      data-testid="lead-notification-email-preview"
      // Stored send-time snapshot; sanitized before render.
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  );
}

EmailPreviewFrame.propTypes = {
  html: PropTypes.string,
  text: PropTypes.string,
  className: PropTypes.string,
};

function LeadNotificationSection({ enrollmentId, notification, fallback }) {
  const [fullOpen, setFullOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const n = notification;

  const onCopy = async () => {
    if (!n) return;
    const raw = n.body_html || n.body_text || "";
    const payload = stripHtml(raw);
    try {
      await navigator.clipboard.writeText(payload);
      toast.success("Email content copied");
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  const onDownload = async (kind) => {
    if (!enrollmentId) return;
    setBusy(kind);
    try {
      if (kind === "html") await reportsApi.downloadNotificationHtml(enrollmentId);
      else await reportsApi.downloadNotificationPdf(enrollmentId);
      toast.success(kind === "html" ? "HTML downloaded" : "PDF downloaded");
    } catch (e) {
      toast.error(e.message || "Download failed");
    } finally {
      setBusy(null);
    }
  };

  if (!n) {
    return (
      <DetailSection title="Lead Notification">
        <Row label="Delivery Status" value={fallback?.email_sent_label || "Pending"} />
        <Row label="Recipient Email" value={fallback?.to} />
        <Row label="Email Sent Date & Time" value={formatDateTime(fallback?.notified_at)} />
        <Row label="Reason" value={fallback?.reason || fallback?.error} />
        <p className="m-0 pt-1 text-[12px] text-slate-500">
          No stored email snapshot is available. Newly qualified leads keep a permanent copy of the
          rendered notification for reporting.
        </p>
      </DetailSection>
    );
  }

  return (
    <>
      <DetailSection
        title="Lead Notification"
        actions={(
          <div className="flex flex-wrap gap-1">
            <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-[11px]" onClick={() => setFullOpen(true)}>
              <Expand size={12} /> View Full Email
            </Button>
            <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-[11px]" onClick={onCopy}>
              <Copy size={12} /> Copy Email
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-[11px]"
              disabled={!!busy}
              onClick={() => onDownload("html")}
            >
              {busy === "html" ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
              HTML
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-[11px]"
              disabled={!!busy}
              onClick={() => onDownload("pdf")}
            >
              {busy === "pdf" ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
              PDF
            </Button>
          </div>
        )}
      >
        <Row label="Recipient Name" value={n.recipient_name} />
        <Row label="Recipient Email" value={n.recipient_email} />
        <Row label="Email Subject" value={n.subject} />
        <Row label="Email Sent Date & Time" value={formatDateTime(n.sent_at)} />
        <Row
          label="Delivery Status"
          value={(
            <span className={cn(
              "inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset",
              deliveryBadgeClass(n.delivery_status),
            )}
            >
              {n.delivery_status_label}
            </span>
          )}
        />
        {n.delivery_status === "failed" && n.failure_reason ? (
          <Row label="Reason" value={n.failure_reason} />
        ) : null}
        <Row label="Notification Trigger" value={n.notification_trigger} />
        {n.provider_message_id ? (
          <Row label="Provider Message ID" value={<span className="font-mono text-[11px]">{n.provider_message_id}</span>} />
        ) : null}

        <div className="pt-3 space-y-2">
          <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-500">Email Preview</div>
          <EmailPreviewFrame html={n.body_html} text={n.body_text} className="max-h-64" />
        </div>
      </DetailSection>

      <Dialog open={fullOpen} onOpenChange={setFullOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="lead-notification-full-email">
          <DialogHeader>
            <DialogTitle>{n.subject || "Lead Notification"}</DialogTitle>
            <DialogDescription>
              Stored send-time snapshot · {n.delivery_status_label} · {formatDateTime(n.sent_at)}
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-auto">
            <EmailPreviewFrame html={n.body_html} text={n.body_text} className="max-h-[65vh]" />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

LeadNotificationSection.propTypes = {
  enrollmentId: PropTypes.string,
  notification: PropTypes.object,
  fallback: PropTypes.object,
};

export function LeadReportDetailSheet({ enrollmentId, open, onOpenChange }) {
  const [state, setState] = useState({ loading: false, error: null, lead: null });

  useEffect(() => {
    if (!open || !enrollmentId) return undefined;
    let cancelled = false;
    setState({ loading: true, error: null, lead: null });
    reportsApi.getLead(enrollmentId)
      .then((data) => {
        if (!cancelled) setState({ loading: false, error: null, lead: data.lead });
      })
      .catch((e) => {
        if (!cancelled) setState({ loading: false, error: e.message || "Failed to load", lead: null });
      });
    return () => { cancelled = true; };
  }, [open, enrollmentId]);

  const lead = state.lead;
  const notify = lead?.notification || {};

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto"
        data-testid="lead-report-detail-sheet"
      >
        <SheetHeader className="text-left pr-8">
          <SheetTitle>{lead?.opportunity_name || "Lead details"}</SheetTitle>
          <SheetDescription>
            Qualified opportunity details, stored Lead Notification email, and timeline.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-5 pb-8">
          {state.loading && (
            <div className="flex items-center gap-2 text-slate-500 text-sm py-8 justify-center">
              <Loader2 className="animate-spin" size={16} /> Loading…
            </div>
          )}
          {state.error && (
            <p className="text-sm text-red-600" data-testid="lead-report-detail-error">{state.error}</p>
          )}
          {!state.loading && !state.error && lead && (
            <>
              <DetailSection title="Opportunity">
                <Row label="Opportunity ID" value={<span className="font-mono text-[12px]">{lead.opportunity_id}</span>} />
                <Row label="Name" value={lead.opportunity_name} />
                <Row label="Status" value={lead.current_status} />
                <Row label="Lead type" value={lead.lead_type} />
                <Row label="Qualified" value={formatDateTime(lead.qualification_date)} />
                <Row label="Last updated" value={formatDateTime(lead.last_updated)} />
                <Row label="Step" value={lead.current_step_label} />
              </DetailSection>

              <DetailSection title="Contact">
                <Row label="Name" value={lead.contact_name} />
                <Row label="Email" value={lead.email} />
                <Row label="Phone" value={lead.phone} />
                <Row label="Channel" value={lead.channel} />
                <Row label="Recipient" value={lead.recipient} />
                <Row label="Title" value={lead.job_title} />
              </DetailSection>

              <DetailSection title="Company">
                <Row label="Company" value={lead.company} />
                <Row label="City" value={lead.city} />
                <Row label="Country" value={lead.country} />
              </DetailSection>

              <DetailSection title="Signal & campaign">
                <Row label="Signal source" value={lead.signal_source} />
                <Row label="Assigned user" value={lead.assigned_user} />
                <Row label="Campaign" value={lead.campaign?.name || lead.campaign_name} />
                <Row label="Campaign status" value={lead.campaign?.status} />
                <Row
                  label="Workflow"
                  value={
                    lead.campaign?.workflow?.has_workflow
                      ? `${lead.campaign.workflow.node_count} nodes`
                      : "—"
                  }
                />
                {lead.detail_path && (
                  <div className="pt-1">
                    <Link
                      to={lead.detail_path}
                      className="text-indigo-600 hover:underline text-[13px]"
                      onClick={() => onOpenChange(false)}
                    >
                      Open full opportunity page
                    </Link>
                  </div>
                )}
              </DetailSection>

              <LeadNotificationSection
                enrollmentId={lead.opportunity_id || enrollmentId}
                notification={lead.lead_notification}
                fallback={{
                  email_sent_label: lead.email_sent_label,
                  to: notify.to,
                  notified_at: notify.notified_at,
                  reason: notify.reason,
                  error: notify.error,
                }}
              />

              {lead.notes ? (
                <DetailSection title="Notes">
                  <p className="whitespace-pre-wrap m-0">{lead.notes}</p>
                </DetailSection>
              ) : null}

              <DetailSection title="Timeline">
                {(lead.timeline || []).length === 0 && (
                  <p className="m-0 text-slate-500">No events recorded.</p>
                )}
                <ul className="m-0 p-0 list-none space-y-2">
                  {(lead.timeline || []).map((ev) => (
                    <li key={`${ev.event_type}-${ev.created_at}`} className="border-b border-slate-200/80 pb-2 last:border-0">
                      <div className="font-medium text-slate-800">{ev.event_type}</div>
                      <div className="text-[12px] text-slate-500">{formatDateTime(ev.created_at)}</div>
                    </li>
                  ))}
                </ul>
              </DetailSection>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

LeadReportDetailSheet.propTypes = {
  enrollmentId: PropTypes.string,
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
};
